import axios, { AxiosError, AxiosResponse } from "axios";
import { datadogRum } from "@datadog/browser-rum";
import { maybeOpenPaywallFromApiError } from "../utils/billingFreezePaywall";

const axiosInstance = axios.create();

// Track if we're currently checking session validity to prevent multiple simultaneous checks
let isCheckingSession = false;

/**
 * A WAF block page, CDN error page, or misrouted marketing page arrives as a
 * full HTML document. Axios's default transform silently hands that document
 * through as a string, and from there it can end up rendered as garbled text
 * or persisted into Redux. Detect it by shape, not content-type, because
 * some endpoints legitimately return bare strings (e.g. an account uuid).
 */
const HTML_DOCUMENT_RE = /^\s*(<!doctype\s+html|<html[\s>])/i;

const isHtmlDocumentBody = (data: unknown): data is string =>
  typeof data === "string" && HTML_DOCUMENT_RE.test(data.slice(0, 500));

const HTML_BODY_SAFE_MESSAGE =
  "The service returned an unexpected response. Please try again in a moment.";

/**
 * Surfaces which endpoint served HTML (WAF? CDN? marketing site?) in Datadog.
 * @param {string | undefined} url - Request URL that produced the HTML body.
 * @param {number | undefined} status - HTTP status of that response.
 * @param {string} body - The HTML document body (only a snippet is reported).
 */
const reportHtmlBody = (
  url: string | undefined,
  status: number | undefined,
  body: string,
): void => {
  const label = `[api] HTML document body from ${url ?? "unknown-url"} (HTTP ${
    status ?? "?"
  })`;
  console.error(label);
  try {
    datadogRum.addError(new Error(label), {
      context: "api.html_body",
      requestUrl: url ?? "",
      status: status ?? 0,
      bodySnippet: body.slice(0, 300),
    });
  } catch {
    /* diagnostics must never break the request path */
  }
};

const htmlBodyError = (response: AxiosResponse): Error => {
  const error = new Error(HTML_BODY_SAFE_MESSAGE) as Error & {
    response: AxiosResponse;
  };
  error.response = {
    ...response,
    data: { success: false, message: HTML_BODY_SAFE_MESSAGE },
  };
  return error;
};

axiosInstance.interceptors.request.use(
  (config) => {
    return config;
  },
  (error: AxiosError) => {
    // Handle request errors here
    return Promise.reject(error);
  },
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    if (isHtmlDocumentBody(response.data)) {
      // An HTML page with a 2xx status would flow into app state (and
      // redux-persist) as a giant string. Convert it into a clean failure.
      reportHtmlBody(response.config?.url, response.status, response.data);
      return Promise.reject(htmlBodyError(response));
    }
    return response;
  },
  async (error: AxiosError) => {
    if (error.response && isHtmlDocumentBody(error.response.data)) {
      // Error pages (WAF block, CDN 5xx) must not reach notifications as a
      // wall of markup — replace the body with a clean message.
      reportHtmlBody(
        error.config?.url,
        error.response.status,
        error.response.data,
      );
      error.response.data = { success: false, message: HTML_BODY_SAFE_MESSAGE };
    }
    // Frozen account: funnel writes to Pay outstanding invoice (portal SoT).
    maybeOpenPaywallFromApiError(error);
    // Handle response errors here
    if (error.response && error.response.status === 401) {
      // Don't redirect if we're already on the landing page to prevent redirect loops
      const currentPath = window.location.pathname;
      if (currentPath === "/" || currentPath === "/login") {
        // Already on login/landing page, don't redirect
        return Promise.reject(error);
      }

      // Don't redirect for the verify endpoint - it's used to check auth status
      const requestUrl = error.config?.url || "";
      if (requestUrl.includes("/auth/business/portal/verify")) {
        // This is the verify endpoint, let it handle the 401 gracefully
        return Promise.reject(error);
      }

      // Image upload (e.g. Express wizard before login) — let callers show an error instead of
      // sending the user to the landing page via window.location.
      if (
        requestUrl.includes("s3/upload") ||
        requestUrl.includes("s3/presigned-upload-url")
      ) {
        return Promise.reject(error);
      }

      // Before redirecting, verify that the session is actually invalid
      // This prevents logging out users when a 401 is just a temporary issue
      if (!isCheckingSession) {
        isCheckingSession = true;
        let shouldRedirect = true;

        try {
          // Check if session is still valid by calling verify endpoint
          // Use plain axios (not axiosInstance) to avoid triggering this interceptor again
          const verifyResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}v3/auth/business/portal/verify`,
            {
              withCredentials: true,
              headers: {
                "api-key": process.env.REACT_APP_API_KEY,
              },
            },
          );

          // If session is still valid, don't redirect - just reject the error
          if (verifyResponse.data.authenticated) {
            shouldRedirect = false;
            isCheckingSession = false;
            return Promise.reject(error);
          }
        } catch (verifyError) {
          // If verify also fails, session is definitely invalid - proceed with redirect
          console.log("Session verification failed, redirecting to login");
          shouldRedirect = true;
        } finally {
          isCheckingSession = false;

          // Only redirect if session is confirmed invalid
          if (shouldRedirect) {
            window.location.href = "/";
          }
        }
      } else {
        // If we're already checking, just reject the error to avoid multiple redirects
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  },
);

export default axiosInstance;
